import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MatchingJobListing {
  id: string;
  title: string;
  salary_min: number | null;
  salary_max: number | null;
  job_category: string;
  employment_type: string | null;
  city: string | null;
  country: string | null;
  company_name: string | null;
  listing_type: string;
  photos: string[] | null;
}

export const useMatchingJobListings = (
  requestCategory: string | null,
  requestListingType: string, // 'job_seeker' or 'employer'
  userId: string | null
) => {
  const [matchingListings, setMatchingListings] = useState<MatchingJobListing[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (userId) {
      fetchMatchingListings();
    }
  }, [requestCategory, requestListingType, userId]);

  const fetchMatchingListings = async () => {
    if (!userId) return;

    try {
      setLoading(true);

      // For job seekers, show employer listings (positions)
      // For employers, show job seeker listings (candidates)
      const matchingType = requestListingType === 'job_seeker' ? 'employer' : 'job_seeker';

      let query = supabase
        .from('job_listings')
        .select('*')
        .eq('user_id', userId)
        .eq('listing_type', matchingType)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(10);

      // Add category filter if specified
      if (requestCategory) {
        query = query.eq('job_category', requestCategory);
      }

      const { data, error } = await query;

      if (error) throw error;

      setMatchingListings(data || []);
    } catch (error) {
      console.error('Error fetching matching job listings:', error);
      toast({
        title: "Error loading listings",
        description: "Failed to load your matching job listings.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return { matchingListings, loading, refetch: fetchMatchingListings };
};
